import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { AllPatentDiagrams } from '../components/PatentDiagrams';
import './PatentApplication.css';

function PatentApplication({ user }) {
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [agreementTimestamp, setAgreementTimestamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState('');
  const [signatureError, setSignatureError] = useState('');
  const [storedSignature, setStoredSignature] = useState(null);
  const [storedUserInfo, setStoredUserInfo] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('v1'); // 'v1' or 'v2'
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [showSuccessAnalysis, setShowSuccessAnalysis] = useState(false);
  const [showTool, setShowTool] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [showDiagrams, setShowDiagrams] = useState(false);

  // Accordion handlers that close all others when opening one
  const handleTogglePrompt = () => {
    if (!showPrompt) {
      setShowSuccessAnalysis(false);
      setShowTool(false);
      setShowGuidance(false);
      setShowDiagrams(false);
    }
    setShowPrompt(!showPrompt);
  };

  const handleToggleSuccessAnalysis = () => {
    if (!showSuccessAnalysis) {
      setShowPrompt(false);
      setShowTool(false);
      setShowGuidance(false);
      setShowDiagrams(false);
    }
    setShowSuccessAnalysis(!showSuccessAnalysis);
  };

  const handleToggleTool = () => {
    if (!showTool) {
      setShowPrompt(false);
      setShowSuccessAnalysis(false);
      setShowGuidance(false);
      setShowDiagrams(false);
    }
    setShowTool(!showTool);
  };

  const handleToggleGuidance = () => {
    if (!showGuidance) {
      setShowPrompt(false);
      setShowSuccessAnalysis(false);
      setShowTool(false);
      setShowDiagrams(false);
    }
    setShowGuidance(!showGuidance);
  };

  const handleToggleDiagrams = () => {
    if (!showDiagrams) {
      setShowPrompt(false);
      setShowSuccessAnalysis(false);
      setShowTool(false);
      setShowGuidance(false);
    }
    setShowDiagrams(!showDiagrams);
  };

  // Get formatted user name for display
  const getUserDisplayName = () => {
    if (!user) return '';
    const userName = user.displayName || 
                    (user.email ? user.email.split('@')[0].replace(/[._]/g, ' ') : '') ||
                    'User';
    // Capitalize first letter of each word
    return userName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Check if user has previously accepted NDA (stored in Firestore)
  useEffect(() => {
    const checkNDAAcceptance = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log('PatentApplication: Checking NDA status for user:', user.email);
        const ndaDocRef = doc(db, 'patentNDAAcceptances', user.uid);
        const ndaDoc = await getDoc(ndaDocRef);

        if (ndaDoc.exists()) {
          const data = ndaDoc.data();
          const acceptedDate = data.acceptedAt?.toDate();
          
          if (acceptedDate) {
            // Check if acceptance is still valid (30 days)
            const daysSinceAcceptance = (new Date() - acceptedDate) / (1000 * 60 * 60 * 24);
            if (daysSinceAcceptance < 30) {
              console.log('PatentApplication: NDA previously accepted, still valid');
              setNdaAccepted(true);
              setAgreementTimestamp(acceptedDate.toISOString());
              // Store signature and user info for display
              setStoredSignature(data.signature || null);
              setStoredUserInfo({
                email: data.userEmail,
                name: data.userName,
                userId: data.userId
              });
            } else {
              console.log('PatentApplication: NDA expired, requiring re-acceptance');
            }
          }
        } else {
          console.log('PatentApplication: No previous NDA acceptance found');
        }
      } catch (error) {
        console.error('Error checking NDA acceptance:', error);
      } finally {
        setLoading(false);
      }
    };

    checkNDAAcceptance();
  }, [user]);

  const handleAcceptNDA = async () => {
    // Validate signature
    if (!signature || signature.trim().length < 3) {
      setSignatureError('Please enter your full name as your signature (minimum 3 characters)');
      return;
    }

    if (!user) {
      setSignatureError('You must be logged in to accept the NDA');
      return;
    }

    setSignatureError('');
    setLoading(true);

    try {
      const timestamp = new Date();
      
      // Store acceptance in Firestore with user identification
      const ndaDocRef = doc(db, 'patentNDAAcceptances', user.uid);
      
      const acceptanceData = {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || signature.trim(),
        signature: signature.trim(),
        acceptedAt: serverTimestamp(),
        acceptedAtISO: timestamp.toISOString(),
        userAgent: navigator.userAgent,
        version: '1.0',
        expiresAt: new Date(timestamp.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      };

      console.log('📝 Attempting to save NDA acceptance:', {
        userId: user.uid,
        email: user.email,
        signature: signature.trim()
      });

      await setDoc(ndaDocRef, acceptanceData, { merge: false }); // Don't merge - create new document each time

      console.log('✅ NDA acceptance stored in Firestore for user:', user.email);
      
      setNdaAccepted(true);
      setAgreementTimestamp(timestamp.toISOString());
    } catch (error) {
      console.error('❌ Error storing NDA acceptance:', error);
      console.error('   Error code:', error.code);
      console.error('   Error message:', error.message);
      console.error('   Full error:', error);
      
      let errorMessage = 'Failed to save acceptance. ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check Firestore security rules.';
      } else if (error.code === 'unavailable') {
        errorMessage += 'Service temporarily unavailable. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again. If the problem persists, contact support.';
      }
      
      setSignatureError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineNDA = () => {
    window.history.back();
  };

  const copyPromptToClipboard = () => {
    const promptText = `I am preparing to file a Provisional Patent Application (PPA) for an interactive data cleansing and normalization system. I have existing technical documentation that I'd like you to review, but I want to approach this from a fresh perspective focused on the data cleansing application rather than the specific implementation context.

## The Invention

I've developed an **Interactive Visual Pattern Selection System for Data Normalization** that allows users to:

1. **Visually decompose** data strings into component parts (e.g., "03 - The Monkees - I'm A Believer" → ["03", "The Monkees", "I'm A Believer"])
2. **Interactively select** which parts to keep through a graphical interface (clicking numbered buttons/parts)
3. **Automatically learn** the selection pattern and apply it to similar records in batch operations
4. **Work across domains** without requiring domain-specific programming (music metadata, medical billing, financial data, customer records, product catalogs, etc.)

## Key Innovation

The novel aspect is the **combination** of:
- Visual decomposition into numbered, selectable parts
- Interactive selection that generates structural patterns (not content-based)
- Automatic pattern learning from user selections
- Structural pattern matching (based on separator type and part count) for batch application

This is distinct from prior art because:
- It's not a black-box automated system (users have control)
- It doesn't require programming knowledge (visual interface)
- It learns patterns from structure, not content
- It works across diverse data types without modification

## Existing Documentation

I have three documents in my repository that provide technical details:

1. **\`docs/PATENT-SUMMARY.md\`** - Executive summary with claims overview, applications, timeline, and cost estimates
2. **\`docs/PATENT-TECHNICAL-DESCRIPTION.md\`** - Detailed technical description with novel claims only (Version 1)
3. **\`docs/PATENT-TECHNICAL-DESCRIPTION-V2.md\`** - Extended claims and implementation details (Version 2)

These documents were created in the context of a music metadata management application, but the invention is **domain-agnostic** and applicable to any data cleansing/normalization task.

## What I Need Help With

1. **Review the existing documentation** and provide feedback on:
   - Whether the claims adequately capture the novelty
   - If the technical description is sufficient for a PPA
   - Any gaps or areas that need strengthening

2. **Help prepare the PPA filing**:
   - Review USPTO requirements for provisional patents
   - Identify what needs to be included/excluded
   - Suggest improvements to claims and descriptions
   - Help structure the application for maximum protection

3. **Prior art considerations**:
   - Help identify potential prior art concerns
   - Suggest how to differentiate from existing solutions
   - Recommend search strategies

4. **Application strategy**:
   - Should I file as an individual or through a company?
   - What supporting materials should I include (diagrams, flowcharts, examples)?
   - Timeline recommendations for filing

5. **General guidance**:
   - Best practices for provisional patent applications
   - Common pitfalls to avoid
   - Whether to consult with a patent attorney before filing

## Important Context

- This is a **software/UI invention** (subject to Alice/Mayo framework considerations)
- The invention is **domain-agnostic** - it's a general-purpose data cleansing tool
- I want to focus on the **data cleansing application** rather than any specific implementation context
- I'm looking for practical, actionable advice for filing a PPA

## Questions to Address

1. Are my claims novel enough, or do they need refinement?
2. What level of technical detail is required for a PPA?
3. Should I include diagrams/flowcharts? If so, what kind?
4. How do I best describe the "structural pattern matching" aspect to highlight novelty?
5. What's the best way to frame this to avoid obviousness rejections?
6. Should I file the PPA myself or hire an attorney?

Please review the existing documentation and provide comprehensive guidance on preparing and filing the Provisional Patent Application.`;

    navigator.clipboard.writeText(promptText).then(() => {
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 3000);
    }).catch(err => {
      console.error('Failed to copy prompt:', err);
      alert('Failed to copy prompt. Please copy manually from the text area below.');
    });
  };

  if (loading) {
    return (
      <div className="patent-application-page">
        <div className="loading" style={{ textAlign: 'center', padding: '50px', color: '#333' }}>
          <div className="loading-spinner">📋</div>
          <p>Loading Patent Application...</p>
        </div>
      </div>
    );
  }

  if (!ndaAccepted) {
    return (
      <div className="patent-application-page">
        <div className="nda-gate">
          <div className="nda-container">
            <h1>🔒 Non-Disclosure Agreement</h1>
            {user && (
              <div style={{ 
                background: '#e3f2fd', 
                padding: '15px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                borderLeft: '4px solid #2196f3'
              }}>
                <p style={{ margin: 0, fontSize: '1.1em', color: '#1565c0' }}>
                  <strong>Welcome, {user.displayName || user.email?.split('@')[0] || 'User'}!</strong>
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#555' }}>
                  You are logged in as: <strong>{user.email}</strong>
                </p>
              </div>
            )}
            <div className="nda-content">
              <p className="nda-intro">
                You are about to access proprietary and confidential information regarding a proposed patent application.
                Please read and accept the following Non-Disclosure Agreement before proceeding.
              </p>

              <div className="nda-agreement">
                <h2>NON-DISCLOSURE AGREEMENT</h2>
                
                <p><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>
                
                <p><strong>Parties:</strong></p>
                <ul>
                  <li><strong>Disclosing Party:</strong> Jookbox Project / Patent Applicant</li>
                  <li><strong>Receiving Party:</strong> {user?.email || 'You'} {user?.displayName ? `(${user.displayName})` : ''}</li>
                  <li><strong>User ID:</strong> {user?.uid || 'Not logged in'}</li>
                </ul>

                <h3>1. Definition of Confidential Information</h3>
                <p>
                  "Confidential Information" means all proprietary, technical, business, and legal information 
                  disclosed by the Disclosing Party to the Receiving Party, including but not limited to:
                </p>
                <ul>
                  <li>Patent application materials, technical descriptions, and claims</li>
                  <li>System architecture, algorithms, and implementation details</li>
                  <li>Business strategies, use cases, and market applications</li>
                  <li>Any information marked as "Confidential" or "Proprietary"</li>
                </ul>

                <h3>2. Obligations of Receiving Party</h3>
                <p>The Receiving Party agrees to:</p>
                <ul>
                  <li>Hold all Confidential Information in strict confidence</li>
                  <li>Not disclose, publish, or share any Confidential Information with any third party</li>
                  <li>Not use the Confidential Information for any purpose other than evaluation and review</li>
                  <li>Not reverse engineer, copy, or create derivative works based on the Confidential Information</li>
                  <li>Take reasonable precautions to protect the confidentiality of the information</li>
                  <li>Return or destroy all Confidential Information upon request</li>
                </ul>

                <h3>3. Exclusions</h3>
                <p>This Agreement does not apply to information that:</p>
                <ul>
                  <li>Was publicly known prior to disclosure</li>
                  <li>Becomes publicly known through no breach of this Agreement</li>
                  <li>Was rightfully received from a third party without breach of any confidentiality obligation</li>
                  <li>Was independently developed without use of the Confidential Information</li>
                  <li>Is required to be disclosed by law or court order (with prior notice to Disclosing Party)</li>
                </ul>

                <h3>4. Term</h3>
                <p>
                  This Agreement shall remain in effect for a period of <strong>30 days</strong> from the date of acceptance, 
                  or until the Confidential Information becomes publicly available, whichever occurs first.
                </p>

                <h3>5. Remedies</h3>
                <p>
                  The Receiving Party acknowledges that any breach of this Agreement may cause irreparable harm 
                  to the Disclosing Party, and that monetary damages may be inadequate. The Disclosing Party 
                  shall be entitled to seek injunctive relief and other equitable remedies in addition to any 
                  other remedies available at law.
                </p>

                <h3>6. Governing Law</h3>
                <p>
                  This Agreement shall be governed by and construed in accordance with the laws of the jurisdiction 
                  in which the Disclosing Party is located, without regard to its conflict of law principles.
                </p>

                <h3>7. Entire Agreement</h3>
                <p>
                  This Agreement constitutes the entire agreement between the parties regarding the subject matter 
                  and supersedes all prior agreements, understandings, and communications, whether written or oral.
                </p>

                <div className="nda-warning">
                  <p><strong>⚠️ IMPORTANT:</strong></p>
                  <ul>
                    <li>By accepting this agreement, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions</li>
                    <li>Unauthorized disclosure or use of this information may result in legal action</li>
                    <li>This agreement is legally binding and enforceable</li>
                    <li>Your acceptance will be recorded with a timestamp</li>
                  </ul>
                </div>
              </div>

              <div style={{ marginTop: '20px', padding: '15px', background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '6px' }}>
                {user && (
                  <div style={{ marginBottom: '15px', padding: '10px', background: '#e8f5e9', borderRadius: '6px', borderLeft: '4px solid #4caf50' }}>
                    <p style={{ margin: 0, fontSize: '0.9em', color: '#2e7d32', fontWeight: '500' }}>
                      <strong>Suggested Name:</strong> {getUserDisplayName()}
                    </p>
                  </div>
                )}
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333' }}>
                  Digital Signature (Type your full name):
                </label>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => {
                    setSignature(e.target.value);
                    setSignatureError('');
                  }}
                  placeholder="Enter your full name to sign"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: signatureError ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1em',
                    marginBottom: '5px'
                  }}
                  disabled={loading}
                />
                {signatureError && (
                  <div style={{ color: '#dc3545', fontSize: '0.9em', marginTop: '5px' }}>
                    {signatureError}
                  </div>
                )}
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '8px', marginBottom: '0' }}>
                  By typing your name above, you are providing your digital signature and agreeing to be bound by this NDA.
                </p>
              </div>

              <div className="nda-actions">
                <button 
                  className="btn-decline" 
                  onClick={handleDeclineNDA}
                  disabled={loading}
                >
                  ❌ Decline
                </button>
                <button 
                  className="btn-accept" 
                  onClick={handleAcceptNDA}
                  disabled={loading || !signature.trim()}
                >
                  {loading ? '⏳ Saving...' : '✅ I Accept and Sign'}
                </button>
              </div>

              <p className="nda-footer">
                By clicking "I Accept and Agree", you acknowledge that you have read and understood this 
                Non-Disclosure Agreement and agree to be bound by its terms.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show patent application content after NDA acceptance
  return (
    <div className="patent-application-page">
      <div className="patent-header">
        <h1>📋 Proposed Patent Application</h1>
        <div className="patent-meta">
          <p><strong>Status:</strong> Draft for Patent Application</p>
          <p><strong>NDA Accepted:</strong> {new Date(agreementTimestamp).toLocaleString()}</p>
          {storedUserInfo && (
            <>
              <p><strong>Signed By:</strong> {storedUserInfo.name || storedUserInfo.email}</p>
              <p><strong>User ID:</strong> {storedUserInfo.userId}</p>
              {storedSignature && <p><strong>Digital Signature:</strong> {storedSignature}</p>}
            </>
          )}
          <p><strong>Confidentiality:</strong> Proprietary and Confidential</p>
        </div>
        
        <div style={{ 
          marginTop: '20px', 
          padding: '20px', 
          background: '#f0f7ff', 
          borderRadius: '8px',
          border: '2px solid #667eea',
          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
        }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '15px', 
            fontWeight: '700', 
            color: '#333',
            fontSize: '1.2em'
          }}>
            📑 Select Patent Version:
          </label>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSelectedVersion('v1')}
              style={{
                padding: '12px 24px',
                fontSize: '1.05em',
                fontWeight: '600',
                border: '2px solid',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: selectedVersion === 'v1' ? '#667eea' : '#fff',
                color: selectedVersion === 'v1' ? '#fff' : '#667eea',
                borderColor: '#667eea',
                minWidth: '200px',
                boxShadow: selectedVersion === 'v1' ? '0 4px 12px rgba(102, 126, 234, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (selectedVersion !== 'v1') {
                  e.target.style.backgroundColor = '#e8edff';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedVersion !== 'v1') {
                  e.target.style.backgroundColor = '#fff';
                }
              }}
            >
              Version 1: Novel Claims Only
            </button>
            <button
              onClick={() => setSelectedVersion('v2')}
              style={{
                padding: '12px 24px',
                fontSize: '1.05em',
                fontWeight: '600',
                border: '2px solid',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: selectedVersion === 'v2' ? '#667eea' : '#fff',
                color: selectedVersion === 'v2' ? '#fff' : '#667eea',
                borderColor: '#667eea',
                minWidth: '200px',
                boxShadow: selectedVersion === 'v2' ? '0 4px 12px rgba(102, 126, 234, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (selectedVersion !== 'v2') {
                  e.target.style.backgroundColor = '#e8edff';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedVersion !== 'v2') {
                  e.target.style.backgroundColor = '#fff';
                }
              }}
            >
              Version 2: Extended Claims & Implementation
            </button>
          </div>
          <p style={{ 
            marginTop: '10px', 
            fontSize: '0.9em', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            {selectedVersion === 'v1' 
              ? 'Version 1 focuses on novel claims that meet patentability requirements.'
              : 'Version 2 includes extended claims and implementation details that may not meet novelty thresholds.'}
          </p>
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '20px', 
          background: '#e8f5e9', 
          borderRadius: '8px',
          border: '2px solid #4caf50',
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <label style={{ 
              fontWeight: '700', 
              color: '#333',
              fontSize: '1.2em'
            }}>
              💬 V1 Recommended Prompt for New Chat
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={copyPromptToClipboard}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.95em',
                  fontWeight: '600',
                  border: '2px solid #4caf50',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  backgroundColor: promptCopied ? '#4caf50' : '#fff',
                  color: promptCopied ? '#fff' : '#4caf50',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  if (!promptCopied) {
                    e.target.style.backgroundColor = '#e8f5e9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!promptCopied) {
                    e.target.style.backgroundColor = '#fff';
                  }
                }}
              >
                {promptCopied ? '✅ Copied!' : '📋 Copy Prompt'}
              </button>
              <button
                onClick={handleTogglePrompt}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.95em',
                  fontWeight: '600',
                  border: '2px solid #4caf50',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  backgroundColor: showPrompt ? '#4caf50' : '#fff',
                  color: showPrompt ? '#fff' : '#4caf50',
                  minWidth: '100px'
                }}
                onMouseEnter={(e) => {
                  if (!showPrompt) {
                    e.target.style.backgroundColor = '#e8f5e9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showPrompt) {
                    e.target.style.backgroundColor = '#fff';
                  }
                }}
              >
                {showPrompt ? '▲ Hide' : '▼ Show'}
              </button>
            </div>
          </div>
          <p style={{ 
            fontSize: '0.9em', 
            color: '#2e7d32',
            marginBottom: '10px',
            lineHeight: '1.6'
          }}>
            Use this prompt to start a new chat focused on filing a Provisional Patent Application (PPA). 
            The prompt references your existing documentation but approaches the invention from a fresh, 
            data cleansing application perspective.
          </p>
          {showPrompt && (
            <div style={{
              marginTop: '15px',
              padding: '15px',
              background: '#fff',
              borderRadius: '6px',
              border: '1px solid #4caf50',
              maxHeight: '500px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.85em',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              color: '#000'
            }}>
              {`I am preparing to file a Provisional Patent Application (PPA) for an interactive data cleansing and normalization system. I have existing technical documentation that I'd like you to review, but I want to approach this from a fresh perspective focused on the data cleansing application rather than the specific implementation context.

## The Invention

I've developed an **Interactive Visual Pattern Selection System for Data Normalization** that allows users to:

1. **Visually decompose** data strings into component parts (e.g., "03 - The Monkees - I'm A Believer" → ["03", "The Monkees", "I'm A Believer"])
2. **Interactively select** which parts to keep through a graphical interface (clicking numbered buttons/parts)
3. **Automatically learn** the selection pattern and apply it to similar records in batch operations
4. **Work across domains** without requiring domain-specific programming (music metadata, medical billing, financial data, customer records, product catalogs, etc.)

## Key Innovation

The novel aspect is the **combination** of:
- Visual decomposition into numbered, selectable parts
- Interactive selection that generates structural patterns (not content-based)
- Automatic pattern learning from user selections
- Structural pattern matching (based on separator type and part count) for batch application

This is distinct from prior art because:
- It's not a black-box automated system (users have control)
- It doesn't require programming knowledge (visual interface)
- It learns patterns from structure, not content
- It works across diverse data types without modification

## Existing Documentation

I have three documents in my repository that provide technical details:

1. **\`docs/PATENT-SUMMARY.md\`** - Executive summary with claims overview, applications, timeline, and cost estimates
2. **\`docs/PATENT-TECHNICAL-DESCRIPTION.md\`** - Detailed technical description with novel claims only (Version 1)
3. **\`docs/PATENT-TECHNICAL-DESCRIPTION-V2.md\`** - Extended claims and implementation details (Version 2)

These documents were created in the context of a music metadata management application, but the invention is **domain-agnostic** and applicable to any data cleansing/normalization task.

## What I Need Help With

1. **Review the existing documentation** and provide feedback on:
   - Whether the claims adequately capture the novelty
   - If the technical description is sufficient for a PPA
   - Any gaps or areas that need strengthening

2. **Help prepare the PPA filing**:
   - Review USPTO requirements for provisional patents
   - Identify what needs to be included/excluded
   - Suggest improvements to claims and descriptions
   - Help structure the application for maximum protection

3. **Prior art considerations**:
   - Help identify potential prior art concerns
   - Suggest how to differentiate from existing solutions
   - Recommend search strategies

4. **Application strategy**:
   - Should I file as an individual or through a company?
   - What supporting materials should I include (diagrams, flowcharts, examples)?
   - Timeline recommendations for filing

5. **General guidance**:
   - Best practices for provisional patent applications
   - Common pitfalls to avoid
   - Whether to consult with a patent attorney before filing

## Important Context

- This is a **software/UI invention** (subject to Alice/Mayo framework considerations)
- The invention is **domain-agnostic** - it's a general-purpose data cleansing tool
- I want to focus on the **data cleansing application** rather than any specific implementation context
- I'm looking for practical, actionable advice for filing a PPA

## Questions to Address

1. Are my claims novel enough, or do they need refinement?
2. What level of technical detail is required for a PPA?
3. Should I include diagrams/flowcharts? If so, what kind?
4. How do I best describe the "structural pattern matching" aspect to highlight novelty?
5. What's the best way to frame this to avoid obviousness rejections?
6. Should I file the PPA myself or hire an attorney?

Please review the existing documentation and provide comprehensive guidance on preparing and filing the Provisional Patent Application.`}
            </div>
          )}
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '20px', 
          background: '#f3e5f5', 
          borderRadius: '8px',
          border: '2px solid #9c27b0',
          boxShadow: '0 2px 8px rgba(156, 39, 176, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <label style={{ 
              fontWeight: '700', 
              color: '#333',
              fontSize: '1.2em'
            }}>
              📊 Patent Approval Success Analysis
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{
                padding: '8px 16px',
                fontSize: '1.1em',
                fontWeight: '700',
                background: '#9c27b0',
                color: '#fff',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                60-70% Success Probability
              </div>
              <button
                onClick={handleToggleSuccessAnalysis}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.95em',
                  fontWeight: '600',
                  border: '2px solid #9c27b0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  backgroundColor: showSuccessAnalysis ? '#9c27b0' : '#fff',
                  color: showSuccessAnalysis ? '#fff' : '#7b1fa2',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  if (!showSuccessAnalysis) {
                    e.target.style.backgroundColor = '#f3e5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showSuccessAnalysis) {
                    e.target.style.backgroundColor = '#fff';
                  }
                }}
              >
                {showSuccessAnalysis ? '▲ Hide' : '▼ Show'}
              </button>
            </div>
          </div>
          {showSuccessAnalysis && (
          <div style={{ marginTop: '15px' }}>
            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#e8f5e9',
              borderRadius: '6px',
              borderLeft: '4px solid #4caf50'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32', fontSize: '1.1em' }}>✅ Key Strengths</h4>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#333' }}>
                <li><strong>Novel Combination</strong>: Visual decomposition + Interactive selection + Pattern learning + Structural matching</li>
                <li><strong>Clear Technical Description</strong>: Well-documented with detailed implementation</li>
                <li><strong>Domain-Agnostic</strong>: Broad applicability across industries strengthens patent value</li>
                <li><strong>Practical Application</strong>: Real-world use cases demonstrate utility</li>
                <li><strong>Structural Matching</strong>: Strong differentiator from prior art (content-based vs. structure-based)</li>
              </ul>
            </div>

            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#fff3cd',
              borderRadius: '6px',
              borderLeft: '4px solid #ffc107'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#f57c00', fontSize: '1.1em' }}>⚠️ Main Challenges</h4>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#333' }}>
                <li><strong>Alice/Mayo Framework</strong>: Software patents face higher scrutiny
                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                    <li>Mitigation: Emphasize technical improvements and specific technical problems solved</li>
                  </ul>
                </li>
                <li><strong>Obviousness Concerns</strong>: Examiner may argue combination is obvious
                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                    <li>Mitigation: Show non-obvious technical advantages and unexpected results</li>
                  </ul>
                </li>
                <li><strong>Prior Art</strong>: Similar tools may exist
                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                    <li>Mitigation: Thorough prior art search and clear differentiation</li>
                  </ul>
                </li>
              </ul>
            </div>

            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#e3f2fd',
              borderRadius: '6px',
              borderLeft: '4px solid #2196f3'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1565c0', fontSize: '1.1em' }}>🎯 Overall Assessment</h4>
              <p style={{ margin: '0 0 10px 0', color: '#333', fontSize: '1.05em', fontWeight: '600' }}>
                <strong>Patentability: Moderate to Good (60-70% chance of success)</strong>
              </p>
              <p style={{ margin: '0 0 10px 0', color: '#333' }}>
                <strong>Recommendation: Proceed with PPA filing</strong>
              </p>
              <p style={{ margin: '0', color: '#666', fontSize: '0.95em' }}>
                <strong>Rationale:</strong> Low cost to establish priority ($70-$280), 12 months to evaluate commercial viability, 
                strong technical foundation, can refine in non-provisional application. Even if patent doesn't issue, 
                PPA provides defensive value.
              </p>
            </div>

            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              background: '#f5f5f5', 
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              <p style={{ margin: '0', color: '#333', fontSize: '0.95em' }}>
                <strong>Note:</strong> This assessment is based on the technical description and claims provided. 
                Success probability may increase with proper attorney assistance, thorough prior art search, 
                and strong emphasis on technical improvements over abstract ideas.
              </p>
            </div>
          </div>
          )}
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '25px', 
          background: '#e8f4f8', 
          borderRadius: '8px',
          border: '2px solid #0288d1',
          boxShadow: '0 2px 8px rgba(2, 136, 209, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ 
              margin: '0', 
              color: '#01579b',
              fontSize: '1.5em',
              fontWeight: '700'
            }}>
              💡 The Tool: Revolutionary Data Cleansing for Everyone
            </h2>
            <button
              onClick={handleToggleTool}
              style={{
                padding: '8px 16px',
                fontSize: '0.95em',
                fontWeight: '600',
                border: '2px solid #0288d1',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: showTool ? '#0288d1' : '#fff',
                color: showTool ? '#fff' : '#0277bd',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!showTool) {
                  e.target.style.backgroundColor = '#e8f4f8';
                }
              }}
              onMouseLeave={(e) => {
                if (!showTool) {
                  e.target.style.backgroundColor = '#fff';
                }
              }}
            >
              {showTool ? '▲ Hide' : '▼ Show'}
            </button>
          </div>
          {showTool && (
          <>
            <div>
              <p style={{ 
                margin: '0 0 20px 0', 
                color: '#333',
                fontSize: '1.1em',
                lineHeight: '1.6'
              }}>
                This isn't just another data cleaning tool. It's a breakthrough that makes complex data normalization 
                accessible to anyone—no programming knowledge, no technical expertise, no learning curve. 
                Just point, click, and watch your messy data transform into clean, consistent information.
              </p>
            </div>

            <div style={{ marginBottom: '25px' }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              color: '#0277bd',
              fontSize: '1.3em',
              fontWeight: '600'
            }}>
              The Problem It Solves
            </h3>
            <p style={{ margin: '0 0 15px 0', color: '#333', lineHeight: '1.7' }}>
              Every business, from medical billing offices to music libraries, struggles with inconsistent data. 
              A patient record might say "SMITH, JOHN M" while another says "John M. Smith". A song title might 
              be "03 - The Monkees - I'm A Believer" when you just want "I'm A Believer". Traditional solutions 
              require either:
            </p>
            <ul style={{ margin: '0 0 15px 0', paddingLeft: '25px', color: '#333', lineHeight: '1.7' }}>
              <li><strong>Technical expertise</strong> - Writing regex patterns, SQL queries, or Python scripts</li>
              <li><strong>Manual labor</strong> - Clicking through thousands of records one by one</li>
              <li><strong>Expensive software</strong> - Enterprise tools that require training and still need technical setup</li>
            </ul>
            <p style={{ margin: '0', color: '#333', lineHeight: '1.7' }}>
              Our tool eliminates all of these barriers. It's as simple as looking at your data and clicking 
              the parts you want to keep.
            </p>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              color: '#0277bd',
              fontSize: '1.3em',
              fontWeight: '600'
            }}>
              How It Works: Natural Language Meets Visual Intelligence
            </h3>
            <p style={{ margin: '0 0 15px 0', color: '#333', lineHeight: '1.7' }}>
              Imagine you're looking at a messy customer name: <code style={{ 
                background: '#fff', 
                padding: '2px 6px', 
                borderRadius: '3px',
                color: '#d32f2f'
              }}>"SMITH, JOHN M"</code>
            </p>
            <p style={{ margin: '0 0 15px 0', color: '#333', lineHeight: '1.7' }}>
              Instead of writing code or complex formulas, you simply:
            </p>
            <ol style={{ margin: '0 0 15px 0', paddingLeft: '25px', color: '#333', lineHeight: '1.7' }}>
              <li><strong>See it broken down visually</strong> - The tool automatically splits it into numbered parts: 
                <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>[1] "SMITH"</code>, 
                <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>[2] "JOHN M"</code></li>
              <li><strong>Click what you want</strong> - You click button [1] because you want just the last name</li>
              <li><strong>Watch it learn</strong> - The system understands: "When I see data with a comma separator and two parts, 
                keep the first part"</li>
              <li><strong>Apply everywhere</strong> - Instantly, all similar records are cleaned automatically</li>
            </ol>
            <p style={{ margin: '0', color: '#333', lineHeight: '1.7' }}>
              No typing formulas. No understanding regex. No technical knowledge. Just natural, visual interaction.
            </p>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              color: '#0277bd',
              fontSize: '1.3em',
              fontWeight: '600'
            }}>
              Unique Advantages for Unsophisticated Users
            </h3>
            
            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#fff',
              borderRadius: '6px',
              borderLeft: '4px solid #4caf50'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32', fontSize: '1.1em' }}>
                🎯 Zero Learning Curve
              </h4>
              <p style={{ margin: '0', color: '#333', lineHeight: '1.6' }}>
                Users don't need to learn anything. If they can click a button, they can clean data. 
                The interface speaks in the same language as their data—visual, intuitive, immediate.
              </p>
            </div>

            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#fff',
              borderRadius: '6px',
              borderLeft: '4px solid #2196f3'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1565c0', fontSize: '1.1em' }}>
                🧠 Pattern Intelligence Without Complexity
              </h4>
              <p style={{ margin: '0', color: '#333', lineHeight: '1.6' }}>
                The system learns patterns from structure, not content. This means it works across 
                completely different domains without any configuration. A medical billing clerk can 
                use it the same way a music librarian does—the tool adapts, not the user.
              </p>
            </div>

            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#fff',
              borderRadius: '6px',
              borderLeft: '4px solid #ff9800'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#e65100', fontSize: '1.1em' }}>
                ⚡ Instant Results, No Waiting
              </h4>
              <p style={{ margin: '0', color: '#333', lineHeight: '1.6' }}>
                Traditional tools require setup, configuration, testing. This tool shows you a preview 
                immediately. You see exactly what will happen before you commit. No surprises, no errors, 
                no wasted time.
              </p>
            </div>

            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: '#fff',
              borderRadius: '6px',
              borderLeft: '4px solid #9c27b0'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#6a1b9a', fontSize: '1.1em' }}>
                🌍 Universal Language
              </h4>
              <p style={{ margin: '0', color: '#333', lineHeight: '1.6' }}>
                The tool doesn't care if your data is in English, Spanish, or any language. It doesn't 
                care if it's names, codes, addresses, or song titles. The visual pattern selection 
                approach transcends language and domain boundaries.
              </p>
            </div>

            <div style={{ 
              padding: '15px',
              background: '#fff',
              borderRadius: '6px',
              borderLeft: '4px solid #f44336'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#c62828', fontSize: '1.1em' }}>
                🛡️ Error-Proof by Design
              </h4>
              <p style={{ margin: '0', color: '#333', lineHeight: '1.6' }}>
                Because users see exactly what they're selecting and get an instant preview, mistakes 
                are caught before they happen. No more accidentally deleting important data or applying 
                the wrong transformation to thousands of records.
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              color: '#0277bd',
              fontSize: '1.3em',
              fontWeight: '600'
            }}>
              Real-World Impact
            </h3>
            <p style={{ margin: '0 0 15px 0', color: '#333', lineHeight: '1.7' }}>
              Consider a medical billing office where staff spend hours manually correcting patient names 
              and insurance codes. Or a music library with thousands of inconsistently formatted song titles. 
              Or a customer database with addresses in a dozen different formats.
            </p>
            <p style={{ margin: '0 0 15px 0', color: '#333', lineHeight: '1.7' }}>
              With this tool, a non-technical staff member can:
            </p>
            <ul style={{ margin: '0 0 15px 0', paddingLeft: '25px', color: '#333', lineHeight: '1.7' }}>
              <li>Clean thousands of records in minutes instead of days</li>
              <li>Maintain data quality without IT department involvement</li>
              <li>Handle new data formats instantly without waiting for developer support</li>
              <li>Feel confident they're doing it correctly because they see the results before applying</li>
            </ul>
            <p style={{ margin: '0', color: '#333', lineHeight: '1.7' }}>
              This isn't just a productivity tool—it's empowerment. It puts data quality control in the 
              hands of the people who understand the data best, without requiring them to understand 
              technology.
            </p>
          </div>

          <div style={{ 
            padding: '20px',
            background: '#fff',
            borderRadius: '6px',
            border: '2px solid #0288d1'
          }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              color: '#01579b',
              fontSize: '1.3em',
              fontWeight: '600'
            }}>
              Why This Matters
            </h3>
            <p style={{ margin: '0', color: '#333', lineHeight: '1.7' }}>
              The patent isn't about protecting a clever algorithm or a complex system. It's about protecting 
              a fundamental shift in how humans interact with data cleaning. We're not automating a technical 
              process—we're making a technical process feel completely natural. This is the difference between 
              a tool that requires training and a tool that feels like it was always meant to work this way.
            </p>
            <p style={{ margin: '15px 0 0 0', color: '#333', lineHeight: '1.7' }}>
              For the first time, data normalization is as intuitive as highlighting text in a document. 
              That's not incremental improvement—that's a paradigm shift. And that's worth protecting.
            </p>
          </div>
          </>
          )}
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '20px', 
          background: '#fff3cd', 
          borderRadius: '8px',
          border: '2px solid #ffc107',
          boxShadow: '0 2px 8px rgba(255, 193, 7, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <label style={{ 
              fontWeight: '700', 
              color: '#333',
              fontSize: '1.2em'
            }}>
              📋 PPA Filing Guidance
            </label>
            <button
              onClick={handleToggleGuidance}
              style={{
                padding: '8px 16px',
                fontSize: '0.95em',
                fontWeight: '600',
                border: '2px solid #ffc107',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: showGuidance ? '#ffc107' : '#fff',
                color: showGuidance ? '#fff' : '#856404',
                minWidth: '100px'
              }}
              onMouseEnter={(e) => {
                if (!showGuidance) {
                  e.target.style.backgroundColor = '#fff8e1';
                }
              }}
              onMouseLeave={(e) => {
                if (!showGuidance) {
                  e.target.style.backgroundColor = '#fff';
                }
              }}
            >
              {showGuidance ? '▲ Hide' : '▼ Show'}
            </button>
          </div>
          <p style={{ 
            fontSize: '0.9em', 
            color: '#856404',
            marginBottom: '10px',
            lineHeight: '1.6'
          }}>
            Comprehensive guidance on filing a Provisional Patent Application (PPA), including documentation review, 
            filing requirements, prior art considerations, application strategy, and answers to common questions.
          </p>
          {showGuidance && (
            <div style={{
              marginTop: '15px',
              padding: '20px',
              background: '#fff',
              borderRadius: '6px',
              border: '1px solid #ffc107',
              maxHeight: '600px',
              overflowY: 'auto',
              fontSize: '0.9em',
              lineHeight: '1.8',
              color: '#000'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#856404', marginTop: '0', marginBottom: '15px' }}>Executive Summary</h3>
                <p>
                  Your invention has <strong>strong potential</strong> for patent protection, but faces challenges typical of software/UI inventions post-Alice. 
                  The <strong>combination of elements</strong> is your strongest asset. A Provisional Patent Application (PPA) is recommended to establish 
                  priority while you evaluate commercial viability.
                </p>
                <p>
                  <strong>Recommendation:</strong> File a PPA within 30-60 days after completing a prior art search. Consider attorney assistance for 
                  claim drafting, but the technical description is solid enough for a self-filed PPA if budget is constrained.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#856404', marginBottom: '15px' }}>Key Recommendations</h3>
                <ul style={{ paddingLeft: '25px' }}>
                  <li><strong>File a PPA</strong> - Low cost ($70-$280), high value for establishing priority</li>
                  <li><strong>Create diagrams</strong> - Essential for UI/software inventions (system architecture, method flow, UI mockups)</li>
                  <li><strong>Strengthen structural matching</strong> - Your strongest differentiator needs more technical detail</li>
                  <li><strong>Address Alice/Mayo</strong> - Emphasize technical improvements, not just automation</li>
                  <li><strong>Consider attorney review</strong> - At minimum for claims ($500-$1,500)</li>
                  <li><strong>Do thorough prior art search</strong> - Know your competitive landscape (2-4 weeks)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#856404', marginBottom: '15px' }}>Timeline & Costs</h3>
                <p><strong>Timeline:</strong> 6-8 weeks to file PPA</p>
                <p><strong>Costs:</strong></p>
                <ul style={{ paddingLeft: '25px' }}>
                  <li>Minimal (self-file): $70-$280</li>
                  <li>With attorney review: $570-$1,780</li>
                  <li>Full attorney service: $2,070-$7,280</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#856404', marginBottom: '15px' }}>Required Materials</h3>
                <ul style={{ paddingLeft: '25px' }}>
                  <li>✅ Written Description (You have this)</li>
                  <li>⚠️ Drawings/Diagrams (You need these - system architecture, method flow, UI mockups)</li>
                  <li>✅ Cover Sheet (Standard USPTO form)</li>
                  <li>✅ Filing Fee ($70-$280)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#856404', marginBottom: '15px' }}>Patentability Assessment</h3>
                <p><strong>Overall Assessment:</strong> <strong>Moderate to Good</strong> (60-70% chance of success)</p>
                <p><strong>Strengths:</strong></p>
                <ul style={{ paddingLeft: '25px' }}>
                  <li>Novel combination of elements</li>
                  <li>Clear technical description</li>
                  <li>Domain-agnostic positioning</li>
                  <li>Structural matching differentiator</li>
                </ul>
                <p><strong>Challenges:</strong></p>
                <ul style={{ paddingLeft: '25px' }}>
                  <li>Alice/Mayo framework (software patents face higher scrutiny)</li>
                  <li>Obviousness concerns (examiner may argue combination is obvious)</li>
                  <li>Prior art may exist (thorough search needed)</li>
                </ul>
              </div>

              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: '#e8f5e9', 
                borderRadius: '6px',
                borderLeft: '4px solid #4caf50'
              }}>
                <p style={{ margin: 0, color: '#2e7d32', fontWeight: '600' }}>
                  📄 Full guidance document available in: <code>docs/PPA-FILING-GUIDANCE.md</code>
                </p>
                <p style={{ margin: '10px 0 0 0', fontSize: '0.9em', color: '#2e7d32' }}>
                  The complete document includes detailed answers to all questions, specific wording recommendations, 
                  diagram requirements, prior art search strategy, cost breakdowns, risk assessment, and step-by-step action plan.
                </p>
              </div>
            </div>
          )}
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '20px', 
          background: '#e3f2fd', 
          borderRadius: '8px',
          border: '2px solid #2196f3',
          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <label style={{ 
              fontWeight: '700', 
              color: '#333',
              fontSize: '1.2em'
            }}>
              📊 Patent Application Diagrams
            </label>
            <button
              onClick={handleToggleDiagrams}
              style={{
                padding: '8px 16px',
                fontSize: '0.95em',
                fontWeight: '600',
                border: '2px solid #2196f3',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: showDiagrams ? '#2196f3' : '#fff',
                color: showDiagrams ? '#fff' : '#1565c0',
                minWidth: '100px'
              }}
              onMouseEnter={(e) => {
                if (!showDiagrams) {
                  e.target.style.backgroundColor = '#e3f2fd';
                }
              }}
              onMouseLeave={(e) => {
                if (!showDiagrams) {
                  e.target.style.backgroundColor = '#fff';
                }
              }}
            >
              {showDiagrams ? '▲ Hide' : '▼ Show'}
            </button>
          </div>
          <p style={{ 
            fontSize: '0.9em', 
            color: '#1565c0',
            marginBottom: '10px',
            lineHeight: '1.6'
          }}>
            Essential diagrams for the Provisional Patent Application, including system architecture, method flow, 
            pattern learning process, structural matching algorithm, and UI mockups.
          </p>
          {showDiagrams && (
            <div style={{
              marginTop: '15px',
              padding: '20px',
              background: '#fff',
              borderRadius: '6px',
              border: '1px solid #2196f3'
            }}>
              <AllPatentDiagrams />
            </div>
          )}
        </div>
      </div>

      <div className="patent-content">
        {selectedVersion === 'v1' ? (
          <>
            <section className="patent-section">
              <h2>System and Method for Interactive Visual Pattern Selection and Batch Application for Data Normalization</h2>
              <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px', marginBottom: '20px' }}>
                <strong>Version 1:</strong> Novel Claims Only - Focuses on patentable innovations
              </p>
          
          <h3>Field of Invention</h3>
          <p>
            This invention relates to data processing systems, specifically to user interfaces and methods 
            for interactive data cleaning, normalization, and pattern extraction across heterogeneous data sources.
          </p>
        </section>

        <section className="patent-section">
          <h3>Background of the Invention</h3>
          
          <h4>Problem Statement</h4>
          <p>Data normalization and cleaning is a critical challenge in data processing systems. Existing solutions face several limitations:</p>
          <ol>
            <li><strong>Automated systems</strong> (machine learning, statistical analysis) require training data and may not handle edge cases or domain-specific patterns effectively.</li>
            <li><strong>Manual editing</strong> is time-consuming and error-prone when dealing with large datasets.</li>
            <li><strong>Script-based solutions</strong> require programming knowledge and are difficult to modify for non-technical users.</li>
            <li><strong>Pattern recognition systems</strong> typically operate in a "black box" manner, providing limited user control over the normalization process.</li>
            <li><strong>Existing tools</strong> often require users to write complex regular expressions or transformation rules without visual feedback.</li>
          </ol>

          <h4>Prior Art Limitations</h4>
          <ul>
            <li>Automated systems lack user control and transparency</li>
            <li>Manual editing doesn't scale to large datasets</li>
            <li>Script-based approaches have high technical barriers</li>
            <li>Pattern recognition systems don't provide interactive visual feedback</li>
            <li>No existing solution combines visual pattern decomposition with interactive selection and batch application</li>
          </ul>
        </section>

        <section className="patent-section">
          <h3>Summary of the Invention</h3>
          <p>
            The present invention provides a system and method for interactive visual pattern selection that enables users to:
          </p>
          <ol>
            <li><strong>Visually decompose</strong> data strings into component parts using intelligent separator detection</li>
            <li><strong>Interactively select</strong> which parts to retain through a graphical user interface</li>
            <li><strong>Learn and apply</strong> the selected pattern to similar records in batch operations</li>
            <li><strong>Work across domains</strong> without requiring domain-specific programming knowledge</li>
          </ol>
        </section>

        <section className="patent-section">
          <h3>Detailed Description of the Invention</h3>
          
          <h4>System Architecture</h4>
          
          <h5>Core Components</h5>
          
          <div className="component-box">
            <h6>1. Pattern Decomposition Engine</h6>
            <ul>
              <li>Analyzes input data strings to identify separators (hyphens, underscores, pipes, periods, etc.)</li>
              <li>Intelligently splits strings into logical components</li>
              <li>Handles edge cases (nested separators, variable-length parts)</li>
              <li>Preserves original data structure for reference</li>
            </ul>
          </div>

          <div className="component-box">
            <h6>2. Interactive Visual Selection Interface</h6>
            <ul>
              <li>Displays decomposed parts as selectable visual elements</li>
              <li>Provides real-time feedback on selection</li>
              <li>Shows original and proposed transformed data side-by-side</li>
              <li>Supports multiple selection modes (single part, multiple parts, ranges)</li>
            </ul>
          </div>

          <div className="component-box">
            <h6>3. Pattern Learning Module</h6>
            <ul>
              <li>Captures the user's selection pattern</li>
              <li>Identifies the structural relationship (e.g., "part 2 of 3", "parts 2-3", "all parts except part 1")</li>
              <li>Stores pattern metadata for reuse</li>
            </ul>
          </div>

          <div className="component-box">
            <h6>4. Batch Application Engine</h6>
            <ul>
              <li>Applies learned pattern to similar records</li>
              <li>Validates pattern applicability before batch operations</li>
              <li>Provides preview of changes before committing</li>
              <li>Handles variations in data structure gracefully</li>
            </ul>
          </div>

          <div className="component-box">
            <h6>5. Domain-Agnostic Processing Layer</h6>
            <ul>
              <li>Works with any structured text data</li>
              <li>No domain-specific rules required</li>
              <li>Adapts to different data types (names, codes, addresses, dates, etc.)</li>
            </ul>
          </div>

          <h4>Method Flow</h4>
          
          <div className="method-step">
            <h5>Step 1: Data Input and Analysis</h5>
            <p>User selects a data record or field containing the pattern to be normalized. System receives input data string (e.g., "03 - The Monkees - I'm A Believer"). Pattern decomposition engine analyzes the string structure.</p>
          </div>

          <div className="method-step">
            <h5>Step 2: Separator Detection and Decomposition</h5>
            <p>System identifies separators using multiple strategies:</p>
            <ul>
              <li>Primary separators: <code> - </code>, <code> _ </code>, <code> | </code>, <code> • </code></li>
              <li>Secondary separators: <code>-</code>, <code>_</code>, <code>|</code>, <code>.</code></li>
              <li>Context-aware detection (handles spaces, special characters)</li>
            </ul>
            <p>String is split into component parts:</p>
            <p className="example">Example: "03 - The Monkees - I'm A Believer" → ["03", "The Monkees", "I'm A Believer"]</p>
          </div>

          <div className="method-step">
            <h5>Step 3: Visual Presentation</h5>
            <p>Each part is displayed as a distinct visual element:</p>
            <ul>
              <li>Numbered indicator (1, 2, 3, etc.)</li>
              <li>Part content clearly displayed</li>
              <li>Interactive selection mechanism (buttons, checkboxes, or radio buttons)</li>
            </ul>
            <p>Original string displayed for reference. Preview area shows potential transformation.</p>
          </div>

          <div className="method-step">
            <h5>Step 4: Interactive Selection</h5>
            <p>User clicks on desired part(s) to select. Visual feedback indicates selection state. System updates preview in real-time. User can modify selection before applying.</p>
          </div>

          <div className="method-step">
            <h5>Step 5: Pattern Extraction</h5>
            <p>System captures the selection pattern:</p>
            <ul>
              <li>Which part indices were selected</li>
              <li>Structural relationship (e.g., "part 2", "parts 2-3", "all except part 1")</li>
              <li>Separator information for reconstruction</li>
            </ul>
            <p>Pattern stored as metadata structure.</p>
          </div>

          <div className="method-step">
            <h5>Step 6: Pattern Validation</h5>
            <p>System validates pattern can be applied to similar records. Checks for structural consistency (same number of parts, same separators). Identifies records that match the pattern structure. Flags records that may not match for user review.</p>
          </div>

          <div className="method-step">
            <h5>Step 7: Batch Application</h5>
            <p>System applies pattern to all matching records:</p>
            <ul>
              <li>For each record, splits by same separators</li>
              <li>Extracts parts at selected indices</li>
              <li>Reconstructs normalized string</li>
              <li>Preserves data integrity (no data loss)</li>
            </ul>
            <p>Preview shown before final application. User can review and approve changes.</p>
          </div>

          <div className="method-step">
            <h5>Step 8: Data Update</h5>
            <p>Normalized data written to target system. Original data preserved (for rollback if needed). Audit trail created (what changed, when, why).</p>
          </div>
        </section>

        <section className="patent-section">
          <h3>Advantages Over Prior Art</h3>
          <ol>
            <li><strong>User Control:</strong> Unlike automated systems, users have full control over pattern selection</li>
            <li><strong>Visual Feedback:</strong> Unlike script-based solutions, users see exactly what will change</li>
            <li><strong>No Programming Required:</strong> Unlike regex/script tools, no technical knowledge needed</li>
            <li><strong>Domain Agnostic:</strong> Works across different data types without modification</li>
            <li><strong>Batch Processing:</strong> Efficiently handles large datasets</li>
            <li><strong>Pattern Learning:</strong> Once pattern is identified, can be reused</li>
            <li><strong>Reversible:</strong> Changes can be previewed and rolled back</li>
            <li><strong>Accessible:</strong> Works for users with varying technical skills</li>
          </ol>
        </section>

        <section className="patent-section">
          <h3>Use Cases and Applications</h3>
          
          <div className="use-case">
            <h4>1. Music Metadata Management</h4>
            <ul>
              <li>Normalize song titles: "01 - Artist - Song Name" → "Song Name"</li>
              <li>Extract artist names from various formats</li>
              <li>Clean album metadata across large collections</li>
            </ul>
          </div>

          <div className="use-case">
            <h4>2. Medical Billing</h4>
            <ul>
              <li>Normalize patient names: "SMITH, JOHN M" → standardized format</li>
              <li>Extract diagnosis codes: "E11.9 - Type 2 diabetes" → "E11.9"</li>
              <li>Parse insurance information: "ABC123 - Blue Cross" → extract policy number</li>
            </ul>
          </div>

          <div className="use-case">
            <h4>3. Financial Data Processing</h4>
            <ul>
              <li>Normalize transaction descriptions</li>
              <li>Extract account numbers from various formats</li>
              <li>Clean merchant names</li>
            </ul>
          </div>

          <div className="use-case">
            <h4>4. Customer Data Management</h4>
            <ul>
              <li>Standardize address formats</li>
              <li>Normalize phone numbers</li>
              <li>Clean company names</li>
            </ul>
          </div>

          <div className="use-case">
            <h4>5. Product Catalog Management</h4>
            <ul>
              <li>Normalize product titles</li>
              <li>Extract SKU codes</li>
              <li>Clean category names</li>
            </ul>
          </div>
        </section>

        <section className="patent-section">
          <h3>Draft Patent Claims</h3>
          
          <div className="claim">
            <h4>Claim 1 (Independent)</h4>
            <p>
              A computer-implemented method for interactive data pattern selection and normalization, comprising:
            </p>
            <ul>
              <li>Receiving an input data string from a user</li>
              <li>Automatically detecting one or more separators within the input data string</li>
              <li>Decomposing the input data string into a plurality of component parts based on the detected separators</li>
              <li>Displaying each component part as a distinct selectable visual element in a user interface</li>
              <li>Receiving user selection of one or more component parts through the user interface</li>
              <li>Generating a normalization pattern based on the selected component parts</li>
              <li>Applying the normalization pattern to a plurality of similar data records in a batch operation</li>
            </ul>
          </div>

          <div className="claim">
            <h4>Claim 2 (Dependent)</h4>
            <p>
              The method of Claim 1, wherein the separator detection comprises:
            </p>
            <ul>
              <li>Analyzing the input data string for multiple separator types</li>
              <li>Selecting an optimal separator based on frequency and context</li>
              <li>Handling nested or multiple separator types</li>
            </ul>
          </div>

          <div className="claim">
            <h4>Claim 3 (Dependent)</h4>
            <p>
              The method of Claim 1, wherein the visual elements are displayed as numbered, interactive buttons 
              that change appearance when selected.
            </p>
          </div>

          <div className="claim">
            <h4>Claim 4 (Dependent)</h4>
            <p>
              The method of Claim 1, further comprising:
            </p>
            <ul>
              <li>Displaying a preview of the normalized data before applying the pattern</li>
              <li>Receiving user confirmation before batch application</li>
              <li>Providing an audit trail of all transformations</li>
            </ul>
          </div>

          <div className="claim">
            <h4>Claim 5 (Dependent)</h4>
            <p>
              The method of Claim 1, wherein the pattern learning module stores the normalization pattern 
              for reuse with similar data structures.
            </p>
          </div>

          <div className="claim">
            <h4>Claim 6 (Independent - System)</h4>
            <p>
              A system for interactive data pattern selection, comprising:
            </p>
            <ul>
              <li>A pattern decomposition engine configured to analyze and split data strings</li>
              <li>An interactive visual selection interface for displaying and selecting component parts</li>
              <li>A pattern learning module for capturing and storing normalization patterns</li>
              <li>A batch application engine for applying patterns to multiple records</li>
              <li>A domain-agnostic processing layer that operates across different data types</li>
            </ul>
          </div>

          <div className="claim">
            <h4>Claim 7 (Independent - UI)</h4>
            <p>
              A graphical user interface for data normalization, comprising:
            </p>
            <ul>
              <li>A display area showing component parts of a data string as selectable elements</li>
              <li>Visual feedback indicating selected parts</li>
              <li>A preview area showing original and normalized data</li>
              <li>Controls for applying the pattern to multiple records</li>
            </ul>
          </div>
        </section>

        <section className="patent-section">
          <h3>Document Information</h3>
          <div className="document-info">
            <p><strong>Version:</strong> 1.0</p>
            <p><strong>Date:</strong> December 19, 2024</p>
            <p><strong>Status:</strong> Draft for Patent Application</p>
            <p><strong>Confidentiality:</strong> Proprietary and Confidential</p>
          </div>
          
          <div className="legal-notice">
            <p><strong>⚠️ Legal Notice:</strong></p>
            <p>
              This document is intended as a technical description for patent application purposes. 
              Legal review by a qualified patent attorney is recommended before filing. This information 
              is confidential and proprietary. Unauthorized disclosure or use may result in legal action.
            </p>
          </div>
        </section>
          </>
        ) : (
          <>
            <section className="patent-section">
              <h2>Technical Description: Interactive Visual Pattern Selection System for Data Normalization</h2>
              <h3>Version 2: Extended Claims and Implementation Details</h3>
              <div style={{ 
                background: '#fff3cd', 
                border: '2px solid #ffc107', 
                borderRadius: '8px', 
                padding: '20px', 
                marginBottom: '30px' 
              }}>
                <p style={{ margin: 0, color: '#856404', fontWeight: '600' }}>
                  <strong>⚠️ Note:</strong> This document contains claims and implementation details that, while important for the system, may not meet the novelty threshold for patent protection. These are included for completeness and potential future patent applications or as trade secrets.
                </p>
              </div>
            </section>

            <section className="patent-section">
              <h3>Extended Claims (Version 2)</h3>
              
              <div className="claim">
                <h4>Claim 8 (Implementation Detail - Separator Detection)</h4>
                <p>A method for detecting separators in data strings, comprising:</p>
                <ul>
                  <li>Analyzing the input data string for multiple separator types including hyphens, underscores, pipes, and periods</li>
                  <li>Selecting an optimal separator based on frequency and context</li>
                  <li>Handling nested or multiple separator types within a single string</li>
                  <li>Prioritizing separators with surrounding spaces over those without</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Standard string parsing technique. Separator detection algorithms are well-established in prior art.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 9 (Implementation Detail - Preview Functionality)</h4>
                <p>The method of Claim 1, further comprising:</p>
                <ul>
                  <li>Displaying a preview of the normalized data before applying the pattern</li>
                  <li>Receiving user confirmation before batch application</li>
                  <li>Providing side-by-side comparison of original and normalized data</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Standard UX practice. Preview functionality is common in data editing tools.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 10 (Implementation Detail - Audit Trail)</h4>
                <p>The method of Claim 1, further comprising:</p>
                <ul>
                  <li>Creating an audit trail of all transformations</li>
                  <li>Recording what data changed, when it changed, and why it changed</li>
                  <li>Preserving original data for rollback purposes</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Standard data management practice. Audit trails are required in many enterprise systems.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 11 (Implementation Detail - Pattern Storage)</h4>
                <p>The method of Claim 1, wherein the pattern learning module stores the normalization pattern for reuse with similar data structures, comprising:</p>
                <ul>
                  <li>Storing pattern metadata including separator type and selected part indices</li>
                  <li>Associating patterns with example inputs and outputs</li>
                  <li>Tracking pattern usage statistics</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Standard database functionality. Pattern storage is a straightforward data persistence operation.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 12 (System Architecture - Standard Components)</h4>
                <p>A system for interactive data pattern selection, comprising:</p>
                <ul>
                  <li>A pattern decomposition engine configured to analyze and split data strings</li>
                  <li>An interactive visual selection interface for displaying and selecting component parts</li>
                  <li>A pattern learning module for capturing and storing normalization patterns</li>
                  <li>A batch application engine for applying patterns to multiple records</li>
                  <li>A domain-agnostic processing layer that operates across different data types</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Describes standard system architecture. The novelty lies in the specific combination and interaction, not in the individual components.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 13 (UI Implementation - Standard GUI Elements)</h4>
                <p>A graphical user interface for data normalization, comprising:</p>
                <ul>
                  <li>A display area showing component parts of a data string as selectable elements</li>
                  <li>Visual feedback indicating selected parts through color changes</li>
                  <li>A preview area showing original and normalized data</li>
                  <li>Controls for applying the pattern to multiple records</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Describes standard GUI elements. The novelty is in the specific application to data normalization, not in the UI components themselves.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 14 (Implementation Detail - Batch Processing)</h4>
                <p>A method for batch processing data transformations, comprising:</p>
                <ul>
                  <li>Validating pattern applicability to multiple records</li>
                  <li>Processing records in batches for efficiency</li>
                  <li>Handling records that don't match the pattern structure</li>
                  <li>Providing progress feedback during batch operations</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Standard batch processing techniques. The novelty is in applying these techniques to the specific pattern selection system.
                </p>
              </div>

              <div className="claim">
                <h4>Claim 15 (Implementation Detail - Error Handling)</h4>
                <p>The method of Claim 1, further comprising:</p>
                <ul>
                  <li>Identifying records that may not match the pattern structure</li>
                  <li>Flagging records for user review</li>
                  <li>Handling edge cases gracefully without data loss</li>
                  <li>Providing error messages for failed transformations</li>
                </ul>
                <p style={{ fontStyle: 'italic', color: '#666', marginTop: '10px' }}>
                  <strong>Novelty Assessment:</strong> Standard error handling practices. Important for implementation but not novel.
                </p>
              </div>
            </section>

            <section className="patent-section">
              <h3>Implementation Details (Non-Patentable)</h3>
              
              <div className="component-box">
                <h5>Separator Detection Algorithm</h5>
                <p>The separator detection uses standard string parsing techniques:</p>
                <ul>
                  <li>Regular expression matching for separator identification</li>
                  <li>Frequency analysis to select optimal separator</li>
                  <li>Context-aware selection based on surrounding characters</li>
                </ul>
              </div>

              <div className="component-box">
                <h5>Pattern Storage Structure</h5>
                <p>Standard JSON data structure for pattern persistence:</p>
                <div className="example">
                  {`{
    "patternId": "unique-identifier",
    "separator": " - ",
    "selectedParts": [2],
    "patternType": "single-part",
    "exampleInput": "03 - The Monkees - I'm A Believer",
    "exampleOutput": "The Monkees",
    "createdAt": "timestamp",
    "appliedCount": 0
}`}
                </div>
              </div>

              <div className="component-box">
                <h5>Batch Processing Implementation</h5>
                <p>Standard batch processing techniques:</p>
                <ul>
                  <li>Iterative processing of records</li>
                  <li>Validation before transformation</li>
                  <li>Error collection and reporting</li>
                  <li>Progress tracking</li>
                </ul>
              </div>

              <div className="component-box">
                <h5>UI Component Implementation</h5>
                <p>Standard web technologies:</p>
                <ul>
                  <li>HTML5 for structure</li>
                  <li>CSS3 for styling and visual feedback</li>
                  <li>JavaScript for interactivity</li>
                  <li>Event handling for user interactions</li>
                </ul>
              </div>
            </section>

            <section className="patent-section">
              <h3>Technical Specifications (Implementation Details)</h3>
              
              <h4>System Requirements</h4>
              <ul>
                <li>Web-based interface (HTML5, CSS3, JavaScript)</li>
                <li>Backend processing (Node.js, Python, or similar)</li>
                <li>Database for pattern storage (Firestore, PostgreSQL, etc.)</li>
                <li>RESTful API for data operations</li>
              </ul>

              <h4>Performance Characteristics</h4>
              <ul>
                <li>Handles datasets with 10,000+ records</li>
                <li>Real-time preview updates (&lt; 100ms)</li>
                <li>Batch processing: 1000 records/second</li>
                <li>Pattern matching: O(n) complexity</li>
              </ul>

              <h4>Scalability</h4>
              <ul>
                <li>Horizontal scaling supported</li>
                <li>Cloud-based deployment ready</li>
                <li>API rate limiting for large operations</li>
                <li>Caching for frequently used patterns</li>
              </ul>
            </section>

            <section className="patent-section">
              <h3>Trade Secret Considerations</h3>
              <p>While these implementation details may not be patentable, they may be valuable as:</p>
              <ol>
                <li><strong>Trade Secrets:</strong> Specific algorithms and optimizations</li>
                <li><strong>Competitive Advantage:</strong> Implementation details that provide performance benefits</li>
                <li><strong>Future Patent Applications:</strong> May become patentable when combined with new innovations</li>
                <li><strong>Documentation:</strong> Important for system maintenance and development</li>
              </ol>
            </section>

            <section className="patent-section">
              <h3>Recommendations</h3>
              <ol>
                <li><strong>Keep as Trade Secrets:</strong> Implementation details that provide competitive advantage</li>
                <li><strong>Document Thoroughly:</strong> For internal development and maintenance</li>
                <li><strong>Monitor Prior Art:</strong> These areas may become patentable with new innovations</li>
                <li><strong>Consider Utility Patents:</strong> Some implementation details may qualify for utility patents if they solve specific technical problems</li>
              </ol>
            </section>

            <section className="patent-section">
              <h3>Document Information</h3>
              <div className="document-info">
                <p><strong>Version:</strong> 2.0</p>
                <p><strong>Date:</strong> December 19, 2024</p>
                <p><strong>Status:</strong> Extended Claims and Implementation Details</p>
                <p><strong>Confidentiality:</strong> Proprietary and Confidential</p>
                <p><strong>Relationship:</strong> Companion document to PATENT-TECHNICAL-DESCRIPTION.md (Version 1)</p>
                <p style={{ fontStyle: 'italic', color: '#666' }}>
                  <strong>Note:</strong> This document contains claims and details that were removed from the primary patent application due to lack of novelty. These are maintained for completeness and potential future use.
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default PatentApplication;

